import React, { useMemo } from 'react';
import {
  coordinateToMercatorPoint,
  geometryMercatorBounds,
  geometryVisualCenter,
  hasPolandCoordinate,
  pathForGeometryMercator,
} from '../utils/geo';
import { HOVER, itemColor, metricText } from '../utils/metrics';
import {
  competitionTooltip,
  competitionValueText,
  facilityCompetitionProvider,
  providerColor,
  providerSoftColor,
  providerTextColor,
} from '../utils/competition';
import {
  activityColor,
  activityTextColor,
  activityTooltip,
  activityValueText,
  activityFreshness,
} from '../utils/activity';

const WIDTH = 900;
const HEIGHT = 620;

function truncateLabel(value, max = 30) {
  const text = String(value || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function roadPath(coordinates, bounds) {
  const points = (coordinates || [])
    .map((pair) => coordinateToMercatorPoint(pair?.[0], pair?.[1], bounds, WIDTH, HEIGHT))
    .filter(Boolean);
  if (points.length < 2) return '';
  return `M${points.map((point) => `${point[0].toFixed(1)},${point[1].toFixed(1)}`).join('L')}`;
}

function roadClass(kind) {
  if (['motorway', 'trunk', 'primary'].includes(kind)) return 'is-major';
  if (['secondary', 'tertiary'].includes(kind)) return 'is-secondary';
  return 'is-local';
}

function calloutPlacement(point, name) {
  const labelWidth = clamp(96 + truncateLabel(name, 30).length * 4.4, 150, 245);
  const labelHeight = 43;
  const rightSide = point[0] < WIDTH * 0.64;
  const x = rightSide
    ? clamp(point[0] + 17, 18, WIDTH - labelWidth - 18)
    : clamp(point[0] - labelWidth - 17, 18, WIDTH - labelWidth - 18);
  const y = clamp(point[1] - labelHeight / 2, 18, HEIGHT - labelHeight - 18);
  const lineEndX = rightSide ? x : x + labelWidth;
  const lineEndY = clamp(point[1], y + 8, y + labelHeight - 8);
  return { x, y, labelWidth, labelHeight, lineEndX, lineEndY };
}

function ContextPart({ feature, bounds }) {
  const center = geometryVisualCenter(feature.geometry, bounds, WIDTH, HEIGHT, true);
  const kind = feature?.properties?.context_kind;
  const label = kind === 'town_part' ? 'miasto' : kind === 'rural_part' ? 'obszar wiejski' : '';
  return (
    <g className={`prospecting-commune-context-part is-${kind || 'part'}`} pointerEvents="none">
      <path d={pathForGeometryMercator(feature.geometry, bounds, WIDTH, HEIGHT)} />
      {center && label ? (
        <g className="prospecting-commune-context-label">
          <rect x={center[0] - 45} y={center[1] - 11} width="90" height="22" rx="9" />
          <text x={center[0]} y={center[1] + 0.5} textAnchor="middle" dominantBaseline="middle">{label}</text>
        </g>
      ) : null}
    </g>
  );
}

function UnlocatedShelf({ items, hoveredId, onHover, onSelectEntity, overlay = 'metric' }) {
  if (!items.length) return null;
  const visible = items.slice(0, 5);
  const x = WIDTH - 278;
  const y = 20;
  const rowHeight = 30;
  const height = 34 + visible.length * rowHeight + (items.length > visible.length ? 22 : 0);

  return (
    <g className="prospecting-unlocated-shelf">
      <rect x={x} y={y} width="256" height={height} rx="13" className="prospecting-unlocated-shelf-bg" />
      <text x={x + 13} y={y + 21} className="prospecting-unlocated-title">Placówki bez dokładnego geotagu</text>
      {visible.map((item, index) => {
        const rowY = y + 31 + index * rowHeight;
        const active = hoveredId === String(item.id);
        return (
          <g
            key={item.id}
            className={`prospecting-unlocated-row ${active ? 'is-active' : ''}`}
            onMouseEnter={() => onHover(String(item.id))}
            onMouseLeave={() => onHover(null)}
            onClick={() => onSelectEntity(item)}
          >
            <rect x={x + 8} y={rowY} width="240" height="26" rx="8" />
            <circle
              cx={x + 22}
              cy={rowY + 13}
              r="8"
              style={overlay === 'competition' ? { fill: providerColor(facilityCompetitionProvider(item)) } : overlay === 'activity' ? { fill: activityColor(item.last_activity_at) } : undefined}
            />
            <text x={x + 22} y={rowY + 13.5} textAnchor="middle" dominantBaseline="middle" className="prospecting-unlocated-index">
              {item.map_index || index + 1}
            </text>
            <text x={x + 36} y={rowY + 14} dominantBaseline="middle" className="prospecting-unlocated-name">
              {truncateLabel(item.name, 29)}
            </text>
          </g>
        );
      })}
      {items.length > visible.length ? (
        <text x={x + 15} y={y + height - 8} className="prospecting-unlocated-more">
          +{items.length - visible.length} kolejnych — wszystkie są na liście po prawej
        </text>
      ) : null}
    </g>
  );
}


function CompetitionAreaMarker({ feature, item, bounds }) {
  const summary = item?.competition_summary;
  if (!summary?.known_units_count || !summary?.dominant_provider) return null;
  const point = geometryVisualCenter(feature.geometry, bounds, WIDTH, HEIGHT, true);
  if (!point) return null;
  const providers = (summary.providers || []).slice(0, 3);
  const main = providers[0];
  const countText = summary.known_units_count > 99 ? '99+' : String(summary.known_units_count);
  return (
    <g className="prospecting-competition-area-marker" pointerEvents="none">
      <circle cx={point[0]} cy={point[1]} r="11.5" fill={providerColor(main.provider)} />
      <text
        x={point[0]}
        y={point[1] + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={providerTextColor(main.provider)}
      >{countText}</text>
      {providers.slice(1).map((row, index) => (
        <circle
          key={row.provider}
          cx={point[0] + 8 + index * 7}
          cy={point[1] + 8 - index * 2}
          r="4.6"
          fill={providerColor(row.provider)}
        />
      ))}
    </g>
  );
}

function ActivityAreaMarker({ feature, item, bounds }) {
  const summary = item?.activity_summary;
  if (!summary?.active_count || activityFreshness(summary?.latest_activity_at) <= 0) return null;
  const point = geometryVisualCenter(feature.geometry, bounds, WIDTH, HEIGHT, true);
  if (!point) return null;
  const countText = Number(summary.active_count) > 99 ? '99+' : String(summary.active_count);
  const color = activityColor(summary.latest_activity_at);
  return (
    <g className="prospecting-competition-area-marker prospecting-activity-area-marker" pointerEvents="none">
      <circle cx={point[0]} cy={point[1]} r="11.5" fill={color} />
      <text
        x={point[0]}
        y={point[1] + 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={activityTextColor(summary.latest_activity_at)}
      >{countText}</text>
    </g>
  );
}

export default function MarketMap({
  scope,
  items,
  geometry,
  context,
  metric,
  overlay = 'metric',
  hoveredId,
  selectedEntityId,
  onHover,
  onSelectArea,
  onSelectEntity,
}) {
  const competitionMode = overlay === 'competition';
  const activityMode = overlay === 'activity';
  const maluchMode = overlay === 'maluch';
  const features = useMemo(() => {
    const source = geometry?.features || [];
    return Array.from(new Map(source.map((feature) => [String(feature?.properties?.id || ''), feature])).values())
      .filter((feature) => String(feature?.properties?.id || '') !== '');
  }, [geometry]);
  const uniqueItems = useMemo(() => Array.from(new Map((items || []).map((item) => [String(item.id), item])).values()), [items]);
  const primaryFeatures = useMemo(() => features.filter((feature) => !feature?.properties?.context_part), [features]);
  const contextParts = useMemo(() => features.filter((feature) => Boolean(feature?.properties?.context_part)), [features]);
  // EPSG:3857 / Web Mercator on every map level. Keeping a single projection
  // avoids the visual jump between country/region views and the commune map.
  const mapBounds = useMemo(() => geometryMercatorBounds(primaryFeatures.length ? primaryFeatures : features), [primaryFeatures, features]);
  const itemById = useMemo(() => new Map(uniqueItems.map((item) => [String(item.id), item])), [uniqueItems]);
  const mappedItems = useMemo(() => uniqueItems
    .map((item) => ({
      item,
      point: coordinateToMercatorPoint(item.longitude, item.latitude, mapBounds, WIDTH, HEIGHT),
    }))
    .filter((entry) => Boolean(entry.point)), [uniqueItems, mapBounds]);
  const unlocatedItems = useMemo(() => uniqueItems.filter((item) => (
    !hasPolandCoordinate(item.longitude, item.latitude)
  )), [uniqueItems]);

  const communeMode = scope === 'commune'
    ? mappedItems.length <= 6
      ? 'labelled'
      : mappedItems.length <= 18
        ? 'numbered'
        : 'dense'
    : null;

  if (!features.length && scope !== 'commune') {
    return <div className="prospecting-map-empty">Brak geometrii dla tego obszaru.</div>;
  }

  const communeClipPath = primaryFeatures.map((feature) => pathForGeometryMercator(feature.geometry, mapBounds, WIDTH, HEIGHT)).join(' ');

  return (
    <svg
      className={`prospecting-market-svg ${scope === 'commune' ? 'is-contextual-commune' : ''} ${competitionMode ? 'is-competition' : ''} ${activityMode ? 'is-activity' : ''}`}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      aria-label={competitionMode ? 'Mapa znanej konkurencji' : activityMode ? 'Mapa ostatniej aktywności handlowej' : 'Mapa potencjału rynku'}
    >
      <rect x="0" y="0" width={WIDTH} height={HEIGHT} rx="22" className="prospecting-map-bg" />

      {scope === 'commune' ? (
        <>
          <defs>
            <clipPath id="prospecting-commune-clip">
              <path d={communeClipPath} />
            </clipPath>
          </defs>
          <g clipPath="url(#prospecting-commune-clip)" className="prospecting-vector-basemap">
            <rect x="0" y="0" width={WIDTH} height={HEIGHT} className="prospecting-vector-land" />
            {(context?.roads || []).map((road) => {
              const d = roadPath(road.coordinates, mapBounds);
              return d ? (
                <path
                  key={road.id}
                  d={d}
                  className={`prospecting-context-road ${roadClass(road.kind)}`}
                >
                  {road.name ? <title>{road.name}</title> : null}
                </path>
              ) : null;
            })}
            {(context?.places || []).map((place) => {
              const point = coordinateToMercatorPoint(place.longitude, place.latitude, mapBounds, WIDTH, HEIGHT);
              if (!point) return null;
              return (
                <g key={place.id} className={`prospecting-context-place is-${place.kind || 'place'}`}>
                  <circle cx={point[0]} cy={point[1]} r={place.kind === 'city' || place.kind === 'town' ? 3.2 : 2.2} />
                  <text x={point[0] + 5} y={point[1] - 5}>{place.name}</text>
                </g>
              );
            })}
          </g>

          {primaryFeatures.map((feature) => (
            <path
              key={feature?.properties?.id}
              d={pathForGeometryMercator(feature.geometry, mapBounds, WIDTH, HEIGHT)}
              className="prospecting-map-region prospecting-commune-outline"
              fill="rgba(255,255,255,.08)"
              stroke="#062d5e"
              strokeWidth="3"
              pointerEvents="none"
            />
          ))}
          {contextParts.map((feature) => <ContextPart key={feature?.properties?.id} feature={feature} bounds={mapBounds} />)}
        </>
      ) : features.map((feature) => {
        const id = String(feature?.properties?.id || '');
        const item = itemById.get(id);
        const active = hoveredId === id;
        const dominantProvider = item?.competition_summary?.dominant_provider || null;
        const maluchCount = Number(item?.maluch_count || 0);
        const fill = maluchMode ? (maluchCount >= 20 ? '#bd3f1d' : maluchCount >= 8 ? '#e68b47' : maluchCount > 0 ? '#f8d0a5' : '#ecf0f4') : competitionMode
          ? providerSoftColor(dominantProvider, dominantProvider ? 0.72 : 0.84)
          : activityMode
            ? activityColor(item?.activity_summary?.latest_activity_at)
            : itemColor(item, metric, uniqueItems);
        return (
          <path
            key={id}
            d={pathForGeometryMercator(feature.geometry, mapBounds, WIDTH, HEIGHT)}
            className={`prospecting-map-region ${active ? 'is-hovered' : ''}`}
            fill={fill}
            stroke={active ? HOVER : '#ffffff'}
            strokeWidth={active ? 3.1 : 1.25}
            onMouseEnter={() => onHover(id)}
            onMouseLeave={() => onHover(null)}
            onClick={() => item && onSelectArea(item)}
          >
            <title>{item
              ? (maluchMode ? `${item.name} — MALUCH+: ${maluchCount} projektów` : competitionMode ? competitionTooltip(item, scope) : activityMode ? activityTooltip(item, scope) : `${item.name} — ${metricText(item, metric)}`)
              : feature?.properties?.name || id}</title>
          </path>
        );
      })}

      {competitionMode && scope !== 'commune' ? features.map((feature) => {
        const id = String(feature?.properties?.id || '');
        const item = itemById.get(id);
        return <CompetitionAreaMarker key={`competition-${id}`} feature={feature} item={item} bounds={mapBounds} />;
      }) : null}
      {maluchMode && scope !== 'commune' ? features.map((feature) => {
        const id=String(feature?.properties?.id || '');const item=itemById.get(id);const n=Number(item?.maluch_count||0);
        if(!n)return null;const point=geometryVisualCenter(feature.geometry,mapBounds,WIDTH,HEIGHT,true);if(!point)return null;
        return <g key={`maluch-${id}`} pointerEvents="none"><circle cx={point[0]} cy={point[1]} r="13" fill="#c34f20" stroke="white" strokeWidth="2"/><text x={point[0]} y={point[1]+.7} textAnchor="middle" dominantBaseline="middle" fill="white" fontSize="11" fontWeight="bold">{n>99?'99+':n}</text></g>
      }) : null}
      {activityMode && scope !== 'commune' ? features.map((feature) => {
        const id = String(feature?.properties?.id || '');
        const item = itemById.get(id);
        return <ActivityAreaMarker key={`activity-${id}`} feature={feature} item={item} bounds={mapBounds} />;
      }) : null}

      {scope === 'commune' && uniqueItems.length > 0 ? (
        <g className="prospecting-commune-map-status" pointerEvents="none">
          <rect x="22" y="20" width="285" height="48" rx="12" />
          <text x="36" y="40">
            {competitionMode
              ? `${uniqueItems.length} placówek · ${uniqueItems.filter((item) => facilityCompetitionProvider(item)).length} ze znanym systemem`
              : activityMode
                ? `${uniqueItems.length} placówek · ${uniqueItems.filter((item) => activityFreshness(item.last_activity_at) > 0).length} z kontaktem ≤ 12 mies.`
                : `${uniqueItems.length} placówek${uniqueItems.some((item) => item.is_own_customer) ? ` · ${uniqueItems.filter((item) => item.is_own_customer).length} naszych` : ''}`}
          </text>
          <text x="36" y="57" className="is-muted">
            {mappedItems.length} na mapie · {unlocatedItems.length} bez dokładnej lokalizacji
          </text>
        </g>
      ) : null}

      {scope === 'commune' ? mappedItems.map(({ item, point }, fallbackIndex) => {
        const active = hoveredId === String(item.id) || selectedEntityId === String(item.id);
        const mapIndex = Number(item.map_index || fallbackIndex + 1);
        const provider = competitionMode ? facilityCompetitionProvider(item) : null;
        const color = competitionMode ? providerColor(provider) : activityMode ? activityColor(item.last_activity_at) : itemColor(item, metric, uniqueItems);
        const indexColor = competitionMode ? providerTextColor(provider) : activityMode ? activityTextColor(item.last_activity_at) : undefined;
        const callout = communeMode === 'labelled' ? calloutPlacement(point, item.name) : null;

        return (
          <g
            key={item.id}
            className={`prospecting-map-marker is-${communeMode} ${item.is_own_customer ? 'is-own-customer' : ''} ${active ? 'is-active' : ''}`}
            onMouseEnter={() => onHover(String(item.id))}
            onMouseLeave={() => onHover(null)}
            onClick={() => onSelectEntity(item)}
          >
            {callout ? (
              <>
                <line
                  x1={point[0]}
                  y1={point[1]}
                  x2={callout.lineEndX}
                  y2={callout.lineEndY}
                  className="prospecting-map-callout-line"
                />
                <rect
                  x={callout.x}
                  y={callout.y}
                  width={callout.labelWidth}
                  height={callout.labelHeight}
                  rx="10"
                  className="prospecting-map-callout-bg"
                />
                <text x={callout.x + 12} y={callout.y + 17} className="prospecting-map-callout-name">
                  {truncateLabel(item.name, 30)}
                </text>
                <text x={callout.x + 12} y={callout.y + 33} className="prospecting-map-callout-value">
                  {competitionMode ? competitionValueText(item, 'commune') : activityMode ? activityValueText(item, 'commune') : metricText(item, metric)}
                </text>
              </>
            ) : null}

            {item.is_own_customer && !activityMode ? (
              <circle
                cx={point[0]}
                cy={point[1]}
                r={communeMode === 'dense' ? (active ? 11 : 8.5) : (active ? 16.5 : 14.5)}
                className="prospecting-own-marker-ring"
              />
            ) : null}
            <circle
              cx={point[0]}
              cy={point[1]}
              r={communeMode === 'dense' ? (active ? 7.5 : 5.2) : (active ? 12.5 : 10.5)}
              fill={color}
              stroke={active ? HOVER : '#062d5e'}
              strokeWidth={active ? 3.2 : 2}
            />
            {communeMode !== 'dense' ? (
              <text
                x={point[0]}
                y={point[1] + 0.7}
                className="prospecting-map-marker-index"
                style={(competitionMode || activityMode) ? { fill: indexColor } : undefined}
                textAnchor="middle"
                dominantBaseline="middle"
                pointerEvents="none"
              >
                {item.is_own_customer && !activityMode ? '✓' : mapIndex}
              </text>
            ) : null}
            <title>{competitionMode ? competitionTooltip(item, 'commune') : activityMode ? activityTooltip(item, 'commune') : `${item.name} — ${metricText(item, metric)}`}</title>
          </g>
        );
      }) : null}

      {scope === 'commune' ? (
        <UnlocatedShelf
          items={unlocatedItems}
          hoveredId={hoveredId}
          onHover={onHover}
          onSelectEntity={onSelectEntity}
          overlay={overlay}
        />
      ) : null}

      {scope === 'commune' && context?.status !== 'ok' ? (
        <g className="prospecting-context-warning" pointerEvents="none">
          <rect x={22} y={HEIGHT - 31} width="220" height="20" rx="7" />
          <text x={132} y={HEIGHT - 21} textAnchor="middle" dominantBaseline="middle">Podkład ulic i miejscowości chwilowo niedostępny</text>
        </g>
      ) : null}

      {scope === 'commune' ? (
        <g className="prospecting-map-attribution" pointerEvents="none">
          <rect x={WIDTH - 194} y={HEIGHT - 27} width="174" height="18" rx="7" />
          <text x={WIDTH - 107} y={HEIGHT - 18} textAnchor="middle" dominantBaseline="middle">© OpenStreetMap contributors</text>
        </g>
      ) : null}
    </svg>
  );
}
